import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { PageLoader } from '../../components/ui/PageLoader/PageLoader';
import { CustomerMergeModal } from './components/CustomerMergeModal';
import { useMergeCustomers } from './hooks/useCustomerMutations';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { CustomerListResponse, CustomerSummary } from '../../types/api';
import styles from './CustomerDuplicatesPage.module.css';

interface DuplicateGroup {
  key: string;
  reason: 'phone' | 'name+postcode';
  members: CustomerSummary[];
}

function normalisePhone(p: string | null): string {
  if (!p) return '';
  return p.replace(/\D+/g, '');
}

function normaliseString(s: string | null): string {
  return (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function groupDuplicates(customers: readonly CustomerSummary[]): DuplicateGroup[] {
  const phoneMap = new Map<string, CustomerSummary[]>();
  const nameKeyMap = new Map<string, CustomerSummary[]>();

  for (const c of customers) {
    if (c.deleted_at !== null) continue;
    const phone = normalisePhone(c.phone);
    if (phone.length >= 7) {
      const arr = phoneMap.get(phone) ?? [];
      arr.push(c);
      phoneMap.set(phone, arr);
    }
    const nameKey = `${normaliseString(c.full_name)}|${normaliseString(c.postcode)}`;
    if (nameKey !== '|') {
      const arr = nameKeyMap.get(nameKey) ?? [];
      arr.push(c);
      nameKeyMap.set(nameKey, arr);
    }
  }

  const groups: DuplicateGroup[] = [];
  for (const [phone, members] of phoneMap.entries()) {
    if (members.length < 2) continue;
    groups.push({ key: `phone:${phone}`, reason: 'phone', members });
  }
  for (const [nameKey, members] of nameKeyMap.entries()) {
    if (members.length < 2) continue;
    groups.push({ key: `name:${nameKey}`, reason: 'name+postcode', members });
  }
  return groups;
}

function useAllCustomers() {
  return useQuery({
    queryKey: queryKeys.customers.list({ scan: 'duplicates' }),
    queryFn: async () => {
      const collected: CustomerSummary[] = [];
      let page = 1;
      const limit = 100;
      const MAX_PAGES = 50;
      while (page <= MAX_PAGES) {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        const res = await apiClient.get<CustomerListResponse>(
          `${endpoints.customers.list}?${params.toString()}`,
        );
        collected.push(...res.data.customers);
        if (page >= (res.data.pagination.totalPages ?? 1)) break;
        page += 1;
      }
      return collected;
    },
    staleTime: 60_000,
  });
}

export function CustomerDuplicatesPage(): JSX.Element {
  const navigate = useNavigate();
  const customers = useAllCustomers();
  const merge = useMergeCustomers();

  const [pair, setPair] = useState<{ source: CustomerSummary; target: CustomerSummary } | null>(
    null,
  );

  const groups = useMemo(
    () => (customers.data ? groupDuplicates(customers.data) : []),
    [customers.data],
  );

  if (customers.isLoading) return <PageLoader message="Scanning for duplicates…" />;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Duplicate Customers"
        actions={
          <Button variant="secondary" onClick={() => navigate('/customers')}>
            Back to customers
          </Button>
        }
      />

      <Card>
        <p className={styles.intro}>
          Showing customer records that share a phone number or the same name + postcode.
          Pick one record per pair to merge into the other (the survivor keeps the bookings,
          receipts and history).
        </p>
      </Card>

      {groups.length === 0 ? (
        <Card>
          <div className={styles.emptyState}>
            <Users className={styles.emptyIcon} aria-hidden />
            <p className={styles.emptyText}>No potential duplicates found.</p>
          </div>
        </Card>
      ) : (
        <ul className={styles.groupList}>
          {groups.map((group) => (
            <li key={group.key}>
              <Card>
                <div className={styles.groupHeader}>
                  <Badge variant="warning">
                    {group.reason === 'phone' ? 'Same phone' : 'Same name + postcode'}
                  </Badge>
                  <span className={styles.groupCount}>{group.members.length} records</span>
                </div>
                <ul className={styles.memberList}>
                  {group.members.map((member) => (
                    <li key={member.uuid} className={styles.member}>
                      <div>
                        <button
                          type="button"
                          className={styles.memberName}
                          onClick={() =>
                            navigate(`/customers/${encodeURIComponent(member.uuid)}`)
                          }
                        >
                          {member.full_name}
                        </button>
                        <p className={styles.memberMeta}>
                          {member.phone || '—'} · {member.postcode || '—'} ·{' '}
                          {member.total_jobs} jobs
                        </p>
                      </div>
                      <div className={styles.memberActions}>
                        {group.members
                          .filter((other) => other.uuid !== member.uuid)
                          .slice(0, 1)
                          .map((other) => (
                            <Button
                              key={other.uuid}
                              size="sm"
                              variant="primary"
                              onClick={() => setPair({ source: member, target: other })}
                            >
                              Merge with {other.full_name}
                            </Button>
                          ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <CustomerMergeModal
        open={pair !== null}
        source={pair?.source ?? null}
        target={pair?.target ?? null}
        isLoading={merge.isPending}
        onClose={() => setPair(null)}
        onConfirm={(sourceUuid, targetUuid) => {
          merge.mutate(
            { source_uuid: sourceUuid, target_uuid: targetUuid },
            {
              onSuccess: () => {
                setPair(null);
                void customers.refetch();
              },
            },
          );
        }}
      />
    </div>
  );
}
